import { useEffect, useRef, useState } from 'react';
import { Plus, Copy, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEditorStore, useActivePage } from '../../store/editorStore';
import CanvasElement from './CanvasElement';
import BackgroundRenderer from './BackgroundRenderer';
import ConfirmDialog from '../ConfirmDialog';

const STORY_W = 412;
const STORY_H = 732;
const SIDE_SCALE = 0.50;

function hexToRgba(hex, opacity) {
  if (!hex?.startsWith('#')) return hex || 'transparent';
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
}
const GAP = 44; // px gap between active and side pages

// Compute CSS transform + visual props for each page slot
function slotStyle(step, zoom) {
  const activeHalf = (STORY_W * zoom) / 2;
  const sideHalf = (STORY_W * SIDE_SCALE) / 2;
  const nearOffset = activeHalf + GAP + sideHalf;          // center of ±1 pages
  const farOffset  = nearOffset + STORY_W * SIDE_SCALE + GAP; // center of ±2 pages (off-screen)

  if (step === 0) {
    return {
      transform: `translate(-50%, -50%) scale(${zoom})`,
      opacity: 1,
      zIndex: 10,
      pointerEvents: 'auto',
      filter: 'none',
    };
  }

  const dir = step > 0 ? 1 : -1;
  const abs = Math.abs(step);

  if (abs === 1) {
    return {
      transform: `translate(calc(-50% + ${dir * nearOffset}px), -50%) scale(${SIDE_SCALE})`,
      opacity: 0.40,
      zIndex: 5,
      pointerEvents: 'auto',
      cursor: 'pointer',
      filter: 'blur(0.6px)',
    };
  }

  // abs >= 2 — off-screen, hidden but kept in DOM for smooth transition
  return {
    transform: `translate(calc(-50% + ${dir * farOffset}px), -50%) scale(${SIDE_SCALE * 0.8})`,
    opacity: 0,
    zIndex: 1,
    pointerEvents: 'none',
  };
}

const ctrlBtn = {
  width: 30,
  height: 30,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  color: 'var(--t-text2)',
  cursor: 'pointer',
  border: 'none',
  transition: 'background 0.15s ease',
};

// ── Main Canvas ───────────────────────────────────────────────

export default function Canvas() {
  const story        = useEditorStore((s) => s.story);
  const page         = useActivePage();
  const activePageId = useEditorStore((s) => s.activePageId);
  const setActivePage = useEditorStore((s) => s.setActivePage);
  const previewMode  = useEditorStore((s) => s.previewMode);
  const selectedLayerIds = useEditorStore((s) => s.selectedLayerIds);
  const zoomLevel    = useEditorStore((s) => s.zoomLevel);
  const deselectAll  = useEditorStore((s) => s.deselectAll);
  const addPage      = useEditorStore((s) => s.addPage);
  const deletePage   = useEditorStore((s) => s.deletePage);
  const duplicatePage = useEditorStore((s) => s.duplicatePage);

  // Derive page-dependent values safely (empty defaults when no page)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pages = story ? [...story.pages].sort((a, b) => a.pageIndex - b.pageIndex) : [];
  const activeIndex = pages.findIndex((p) => p.id === activePageId);
  const isPreview = previewMode !== 'edit';

  const sortedLayers = page
    ? [...page.layers].filter((l) => !l.hidden).sort((a, b) => a.transform.zIndex - b.transform.zIndex)
    : [];

  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < pages.length - 1;

  // ── Autoplay logic ────────────────────────────────────────
  const DEFAULT_PAGE_DURATION = 4000;
  const [autoProgress, setAutoProgress] = useState(0);
  const [autoFinished, setAutoFinished] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const autoTimerRef = useRef(null);
  const autoRafRef = useRef(null);
  const autoStartRef = useRef(0);
  const isLastPage = activeIndex === pages.length - 1;

  // Keyboard navigation
  useEffect(() => {
    if (!story || !page) return;
    function onKey(e) {
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'ArrowLeft' && activeIndex > 0) {
        e.preventDefault();
        setActivePage(pages[activeIndex - 1].id);
      }
      if (e.key === 'ArrowRight' && activeIndex < pages.length - 1) {
        e.preventDefault();
        setActivePage(pages[activeIndex + 1].id);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [story, page, activeIndex, pages, setActivePage]);

  useEffect(() => {
    if (!story || !page) return;
    if (previewMode !== 'autoplay') {
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
      if (autoRafRef.current) cancelAnimationFrame(autoRafRef.current);
      setAutoProgress(0);
      setAutoFinished(false);
      return;
    }

    // On the last page: fill bar to 100% and stop
    if (isLastPage) {
      setAutoProgress(100);
      setAutoFinished(true);
      return;
    }

    setAutoFinished(false);
    const duration = page.autoAdvanceDuration || DEFAULT_PAGE_DURATION;
    autoStartRef.current = performance.now();
    setAutoProgress(0);

    function tick(now) {
      const elapsed = now - autoStartRef.current;
      const pct = Math.min(100, (elapsed / duration) * 100);
      setAutoProgress(pct);
      if (pct < 100) {
        autoRafRef.current = requestAnimationFrame(tick);
      }
    }
    autoRafRef.current = requestAnimationFrame(tick);

    autoTimerRef.current = setTimeout(() => {
      setActivePage(pages[activeIndex + 1].id);
    }, duration);

    return () => {
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
      if (autoRafRef.current) cancelAnimationFrame(autoRafRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story, page, previewMode, activePageId]);

  // ── Early return (AFTER all hooks) ────────────────────────
  if (!story || !page) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--t-bg)' }}>
        <p className="text-sm" style={{ color: 'var(--t-muted)' }}>No page selected</p>
      </div>
    );
  }

  function replayAutoplay() {
    setAutoFinished(false);
    setActivePage(pages[0].id);
  }

  function handleDelete() {
    if (!page || pages.length === 1) return;
    setConfirmDeleteOpen(true);
  }

  return (
    <div
      className="w-full h-full overflow-hidden relative"
      style={{ background: 'var(--t-panel)' }}
    >
      {/* ── Carousel ─────────────────────────────────────── */}
      <div className="w-full h-full relative">
        {pages.map((p, index) => {
          const step   = index - activeIndex;
          const styles = slotStyle(step, zoomLevel);
          const isActive = step === 0;

          return (
            <div
              key={p.id}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transformOrigin: 'center center',
                transition: 'transform 0.45s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.45s ease, filter 0.45s ease',
                ...styles,
              }}
              onClick={!isActive ? () => { deselectAll(); setActivePage(p.id); } : undefined}
            >
              {isActive ? (
                /* ── Full editing canvas ── */
                <div
                  className="story-canvas relative overflow-hidden"
                  style={{
                    width: STORY_W,
                    height: STORY_H,
                    borderRadius: previewMode === 'mobile' ? 32 : 14,
                    boxShadow: '0 0 0 2px rgba(99,102,241,0.45), 0 32px 80px rgba(0,0,0,0.45)',
                  }}
                  onClick={(e) => { if (e.target === e.currentTarget) deselectAll(); }}
                >
                  <BackgroundRenderer background={page.background} />

                  {previewMode === 'mobile' && (
                    <div className="absolute inset-0 pointer-events-none z-50">
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-4 bg-black rounded-full opacity-80" />
                    </div>
                  )}

                  {sortedLayers.map((layer) => (
                    <CanvasElement
                      key={layer.id}
                      layer={layer}
                      canvasWidth={STORY_W}
                      canvasHeight={STORY_H}
                      isSelected={selectedLayerIds.includes(layer.id)}
                      isPreviewMode={isPreview}
                    />
                  ))}

                  {isPreview && previewMode === 'autoplay' && (
                    <div className="absolute inset-0 z-40 pointer-events-none">
                      {/* Progress bars */}
                      <div className="absolute top-2 left-2 right-2 flex gap-1">
                        {pages.map((_, i) => (
                          <div key={i} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-white rounded-full"
                              style={{
                                width: i < activeIndex ? '100%'
                                  : i === activeIndex ? `${autoProgress}%`
                                  : '0%',
                                transition: i === activeIndex ? 'none' : undefined,
                              }}
                            />
                          </div>
                        ))}
                      </div>

                      {/* Replay button — shown when story finishes */}
                      {autoFinished && (
                        <button
                          onClick={replayAutoplay}
                          style={{
                            position: 'absolute', top: 12, right: 12,
                            pointerEvents: 'all',
                            background: 'rgba(0,0,0,0.55)',
                            border: '1.5px solid rgba(255,255,255,0.4)',
                            borderRadius: 20,
                            color: '#fff',
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '5px 12px',
                            fontSize: 12, fontWeight: 600,
                            cursor: 'pointer',
                            backdropFilter: 'blur(6px)',
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="1 4 1 10 7 10" />
                            <path d="M3.51 15a9 9 0 1 0 .49-3.5" />
                          </svg>
                          Replay
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* ── Preview card ── */
                <PagePreviewCard page={p} />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Left nav arrow ───────────────────────────────── */}
      {hasPrev && (
        <button
          onClick={() => setActivePage(pages[activeIndex - 1].id)}
          style={{
            position: 'absolute', left: 16, top: '50%',
            transform: 'translateY(-50%)', zIndex: 30,
            width: 38, height: 38, borderRadius: '50%',
            background: 'var(--t-surface)',
            border: '1px solid var(--t-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            color: 'var(--t-text)',
            transition: 'box-shadow 0.2s ease',
          }}
          title="Previous page (←)"
        >
          <ChevronLeft size={18} />
        </button>
      )}

      {/* ── Right nav arrow ──────────────────────────────── */}
      {hasNext && (
        <button
          onClick={() => setActivePage(pages[activeIndex + 1].id)}
          style={{
            position: 'absolute', right: 16, top: '50%',
            transform: 'translateY(-50%)', zIndex: 30,
            width: 38, height: 38, borderRadius: '50%',
            background: 'var(--t-surface)',
            border: '1px solid var(--t-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            color: 'var(--t-text)',
            transition: 'box-shadow 0.2s ease',
          }}
          title="Next page (→)"
        >
          <ChevronRight size={18} />
        </button>
      )}

      {/* ── Edge fade overlays ───────────────────────────── */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 120, background: 'linear-gradient(to right, var(--t-panel), transparent)', pointerEvents: 'none', zIndex: 20 }} />
      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 120, background: 'linear-gradient(to left, var(--t-panel), transparent)', pointerEvents: 'none', zIndex: 20 }} />

      {/* ── Floating page controls ────────────────────────── */}
      {!isPreview && (
        <div
          style={{
            position: 'absolute',
            bottom: 18,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 35,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: 'var(--t-surface)',
            border: '1px solid var(--t-border)',
            borderRadius: 14,
            padding: '5px 10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.14)',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: 11, color: 'var(--t-muted)', paddingRight: 4, fontVariantNumeric: 'tabular-nums' }}>
            {activeIndex + 1} / {pages.length}
          </span>
          <div style={{ width: 1, height: 14, background: 'var(--t-border)', margin: '0 2px' }} />
          <button
            onClick={() => addPage(activeIndex)}
            style={ctrlBtn}
            title="Add page after"
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--t-panel)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Plus size={14} />
          </button>
          <button
            onClick={() => duplicatePage(page.id)}
            style={ctrlBtn}
            title="Duplicate page"
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--t-panel)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Copy size={14} />
          </button>
          <button
            onClick={handleDelete}
            style={{ ...ctrlBtn, color: pages.length === 1 ? 'var(--t-muted)' : '#ef4444' }}
            disabled={pages.length === 1}
            title="Delete page"
            onMouseEnter={(e) => { if (pages.length > 1) e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}

      {/* ── Zoom controls ────────────────────────────────── */}
      <ZoomControls />

      <ConfirmDialog
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => deletePage(page.id)}
        title="Delete page?"
        message="This page will be permanently removed from the story."
        confirmLabel="Delete"
      />
    </div>
  );
}

// ── Page preview card (for non-active slots) ──────────────────

function PagePreviewCard({ page }) {
  return (
    <div
      style={{
        width: STORY_W,
        height: STORY_H,
        borderRadius: 14,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      }}
    >
      <BackgroundRenderer background={page.background} />

      {/* Render simplified layer previews */}
      {page.layers
        .filter((l) => !l.hidden)
        .sort((a, b) => a.transform.zIndex - b.transform.zIndex)
        .slice(0, 10)
        .map((layer) => {
          const t    = layer.transform;
          const data = layer.data;
          const type = data.type;

          if ((type === 'image' || type === 'video') && data.src) {
            return (
              <img
                key={layer.id}
                src={data.src}
                alt=""
                style={{
                  position: 'absolute',
                  left: `${t.x}%`, top: `${t.y}%`,
                  width: `${t.width}%`, height: `${t.height}%`,
                  objectFit: (data.objectFit || 'cover'),
                  pointerEvents: 'none',
                }}
              />
            );
          }

          if (type === 'text') {
            const s = data.style ?? {};
            const padding = s.padding || [0, 0, 0, 0];
            const scale = SIDE_SCALE;
            const justifyContent = s.textAlign === 'left' ? 'flex-start' : s.textAlign === 'right' ? 'flex-end' : 'center';
            const bgStyle = s.backgroundColor
              ? {
                  background: hexToRgba(s.backgroundColor, s.backgroundOpacity ?? 1),
                  padding: `${padding[0] * scale}px ${padding[1] * scale}px ${padding[2] * scale}px ${padding[3] * scale}px`,
                  borderRadius: (s.borderRadius ?? 0) * scale,
                }
              : {
                  padding: `${padding[0] * scale}px ${padding[1] * scale}px ${padding[2] * scale}px ${padding[3] * scale}px`,
                };
            return (
              <div
                key={layer.id}
                style={{
                  position: 'absolute',
                  left: `${t.x}%`, top: `${t.y}%`,
                  width: `${t.width}%`, height: `${t.height}%`,
                  display: 'flex', alignItems: 'center', justifyContent,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                  transform: t.rotation ? `rotate(${t.rotation}deg)` : undefined,
                  opacity: t.opacity ?? 1,
                  zIndex: t.zIndex,
                }}
              >
                <div style={bgStyle}>
                  <p style={{
                    fontFamily: `'${s.fontFamily || 'sans-serif'}', sans-serif`,
                    fontSize: (s.fontSize || 16) * scale,
                    fontWeight: s.fontWeight || '400',
                    lineHeight: s.lineHeight || 1.4,
                    letterSpacing: (s.letterSpacing || 0) * scale,
                    textAlign: s.textAlign || 'left',
                    textTransform: s.textTransform || 'none',
                    color: s.color || '#fff',
                    textShadow: s.textShadow ?? undefined,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0, padding: 0, width: '100%',
                  }}>
                    {data.content}
                  </p>
                </div>
              </div>
            );
          }

          if (type === 'shape') {
            return (
              <div
                key={layer.id}
                style={{
                  position: 'absolute',
                  left: `${t.x}%`, top: `${t.y}%`,
                  width: `${t.width}%`, height: `${t.height}%`,
                  background: data.fill || 'rgba(255,255,255,0.2)',
                  opacity: data.opacity ?? 1,
                  borderRadius: data.shape === 'circle' ? '50%' : 0,
                  pointerEvents: 'none',
                }}
              />
            );
          }

          return null;
        })}

      {/* Interaction blocker */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 10 }} />
    </div>
  );
}

// ── Zoom controls ─────────────────────────────────────────────

function ZoomControls() {
  const zoomLevel = useEditorStore((s) => s.zoomLevel);
  const setZoom   = useEditorStore((s) => s.setZoom);

  return (
    <div
      className="absolute bottom-4 right-4 flex items-center gap-1 rounded-lg p-1"
      style={{
        zIndex: 40,
        background: 'var(--t-surface)',
        border: '1px solid var(--t-border)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      }}
    >
      <button
        onClick={() => setZoom(Math.max(0.3, zoomLevel - 0.1))}
        className="w-6 h-6 flex items-center justify-center rounded text-lg leading-none transition-colors"
        style={{ color: 'var(--t-muted)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--t-panel)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        −
      </button>
      <button
        onClick={() => setZoom(1)}
        className="px-2 py-0.5 text-xs rounded min-w-[40px] text-center transition-colors"
        style={{ color: 'var(--t-muted)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--t-panel)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        {Math.round(zoomLevel * 100)}%
      </button>
      <button
        onClick={() => setZoom(Math.min(2, zoomLevel + 0.1))}
        className="w-6 h-6 flex items-center justify-center rounded text-lg leading-none transition-colors"
        style={{ color: 'var(--t-muted)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--t-panel)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        +
      </button>
    </div>
  );
}
